// Los links para salir del navegador de Instagram, Facebook y compañía sin perder la atribución.
//
// Google no siempre deja iniciar sesión dentro de esos navegadores: Nacho lo midió en PostHog y
// dentro de Instagram falla más o menos a la mitad, dentro de Facebook Android casi siempre.
// El registro muestra un aviso (`AvisoGoogleEnApp`) que ofrece abrir Atlas en el navegador;
// estos son los links que usa y la regla de dónde va (ADR-2026-10-20-google-avisa-en-instagram-sin-frenar,
// ADR-2026-11-02-aviso-de-google-solo-donde-chrome-abre).
// Quién está dentro de qué app lo decide `navegadorEmbebido`.
//
// Todo lo de este archivo es puro, para poder testearlo.

import { UTM_KEYS } from './signupAttribution';
import type { NavegadorEmbebido } from './navegadorEmbebido';

/**
 * ¿Va el aviso con "Abrir en Chrome"? Solo donde el botón saca a la gente de verdad: Android,
 * fuera de Facebook y Messenger. Medido en PostHog del 21/10 al 1/11: desde Instagram llegó a
 * Chrome el 77% de quienes tocaron (99 de 129); desde Facebook, 2 de 48, porque su navegador
 * ignora el `intent://`. En iPhone no hay botón posible y la instrucción del menú no la siguió
 * casi nadie (2 de 115). Ahí el aviso solo estorbaba
 * (ADR-2026-11-02-aviso-de-google-solo-donde-chrome-abre).
 */
export function avisoConChrome(embebido: NavegadorEmbebido | null): boolean {
  return !!embebido && embebido.android && embebido.app !== 'Facebook' && embebido.app !== 'Messenger';
}

/** Lo que la persona tiene guardado de su llegada (las claves `atlas:*` de `signupAttribution`). */
export type AtribucionGuardada = Partial<Record<string, string | null>>;

/** El fbclid sale del `_fbc` (`fb.1.<timestamp>.<fbclid>`): con él, el navegador nuevo rearma la cookie. */
export function fbclidDelFbc(fbc: string | null | undefined): string | null {
  const partes = (fbc ?? '').split('.');
  if (partes.length < 4 || partes[0] !== 'fb') return null;
  const fbclid = partes.slice(3).join('.');
  return fbclid || null;
}

/**
 * La URL actual con la atribución guardada pegada en el query.
 *
 * Al pasar a Chrome o Safari, el navegador nuevo arranca VACÍO: lo que este guardó en
 * localStorage (de qué anuncio vino, el código de afiliado) no viaja. Si no va en el link,
 * arreglar el login rompía la atribución que se arregló en la landing. Se SUMA lo que falta
 * y nunca se pisa lo que la URL ya trae (el alta por link lleva su plan, país y `sid`).
 */
export function urlConAtribucion(href: string, guardado: AtribucionGuardada): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  const sumar = (param: string, valor: string | null | undefined) => {
    if (valor && !url.searchParams.has(param)) url.searchParams.set(param, valor);
  };
  for (const key of UTM_KEYS) sumar(key, guardado[key]);
  sumar('ref', guardado.ref);
  sumar('fbclid', fbclidDelFbc(guardado.fbc));
  return url.toString();
}

/**
 * Marca que viaja en el link al navegador para que ahí la pantalla siga sola a Google: el
 * modal le dijo a la persona "para completar el registro con Google", y hacerla tocar Google
 * otra vez sería un paso de más (ADR-2026-11-02-google-en-instagram-pasa-por-el-navegador).
 */
export const PARAM_CONTINUAR = 'continuar';
const CONTINUAR_CON_GOOGLE = 'google';

/** La URL con la marca de "seguí con Google" puesta. */
export function conContinuarConGoogle(href: string): string {
  try {
    const url = new URL(href);
    url.searchParams.set(PARAM_CONTINUAR, CONTINUAR_CON_GOOGLE);
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * ¿La URL trae la marca? Devuelve también la URL sin ella: se saca ANTES de ir a Google, así
 * la vuelta (que usa la URL actual) o un "atrás" no disparan Google otra vez.
 */
export function leerContinuarConGoogle(href: string): { continuar: boolean; sinMarca: string } {
  try {
    const url = new URL(href);
    if (url.searchParams.get(PARAM_CONTINUAR) !== CONTINUAR_CON_GOOGLE) return { continuar: false, sinMarca: href };
    url.searchParams.delete(PARAM_CONTINUAR);
    return { continuar: true, sinMarca: url.toString() };
  } catch {
    return { continuar: false, sinMarca: href };
  }
}

/**
 * Link que en Android le pide al sistema abrir la URL en Chrome. Si Chrome no está, el
 * sistema cae a `browser_fallback_url` (la misma URL, en el navegador que haya).
 * Instagram y Facebook no garantizan respetarlo: por eso siempre acompañan los pasos a mano.
 */
export function intentParaChrome(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const esquema = url.protocol.replace(':', '');
  const sinEsquema = `${url.host}${url.pathname}${url.search}${url.hash}`;
  return `intent://${sinEsquema}#Intent;scheme=${esquema};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(url.toString())};end`;
}
